import { useAccount } from "@qw/auth";
import {
  type ChatSessionFilterInput,
  type ChatSessionQuery,
  type ChatSessionsQuery,
  type CreateChatSessionInput,
  useChatSessionQuery,
  useChatSessionsQuery,
  useCreateChatSessionMutation,
} from "@qw/graphql";
import { Spinner, useToast } from "@qw/ui";
import {
  createContext,
  type FC,
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { MissingChatSession, type MissingChatSessionProps } from "../components/missing-chat-session";
import { resolveChatSelection } from "./resolve-chat-selection";

type ChatSession = ChatSessionQuery["chatSession"];

export interface ChatSessionContextValue {
  canCreateChatSession: boolean;
  chatSession?: ChatSession | null;
  chatSessions: ChatSessionsQuery["chatSessions"];
  chatSessionsLoading: boolean;
  createChatSessionLoading: boolean;
  handleCreateChatSession: () => Promise<void>;
  onClose?: () => void;
  refetchChatSessions: () => void;
  // Narrowed from `React.Dispatch<React.SetStateAction<...>>` — no
  // consumer uses the functional-updater form (only `chat-session-
  // selector.tsx` calls this externally, and it passes a value).
  // Keeping the value-only shape lets `FilteredChatSessionProvider`
  // store just the selected session id and derive the live entity
  // from `chatSessions` on every render (so `cache.modify` broadcasts
  // from `useChatSessionStatusLiveCache` flow through to the gate).
  setChatSession: (next: ChatSession | null | undefined) => void;
  supportsMultipleSessions?: boolean;
}

export const ChatSessionContext = createContext<ChatSessionContextValue | null>(null);

export interface ChatSessionProviderProps {
  allowCreateChatSession?: boolean;
  /**
   * Exact-mode: render exactly this one session (no list, no selector).
   * Mutually exclusive with the filtered/multi-session props below —
   * when set, the provider ignores `chatSessionsFilter` / `selectedSessionId`.
   */
  chatSessionId?: string | null;
  chatSessionsFilter?: Omit<ChatSessionFilterInput & CreateChatSessionInput, "userId">;
  missingSessionProps?: MissingChatSessionProps;
  onClose?: () => void;
  /**
   * Controlled-selection handler for the filtered (multi-session) mode.
   * When provided, selection is **controlled**: the provider holds no
   * internal selection state and instead reflects {@link selectedSessionId},
   * calling this whenever the active session should change (the user picks
   * a row in `ChatSessionSelector`, the default is adopted on first load,
   * or a freshly-created session is promoted). The consumer is the single
   * source of truth — e.g. the inbox `BookChatPanel` persists the id to
   * URL state via nuqs and feeds it back through `selectedSessionId`,
   * making the URL the one place selection lives (no dual-store sync).
   *
   * Omit it to use **uncontrolled** mode, where the provider owns the
   * selection internally (web book-chat, product/playbook editors).
   */
  onSelectSession?: (next: ChatSession | null) => void;
  /**
   * Controlled-selection value for the filtered mode (see
   * {@link onSelectSession}). The active session is derived from this id
   * against the loaded list, so it is reorder-stable and honors a
   * cold-mount deep-link by construction — no snapshot, no sync effect.
   * Ignored in uncontrolled mode and in exact mode (`chatSessionId`).
   */
  selectedSessionId?: string | null;
  supportsMultipleSessions?: boolean;
}

interface ChatSessionProviderContentProps extends PropsWithChildren {
  error?: string;
  loading: boolean;
  missingSessionProps?: MissingChatSessionProps;
  value: ChatSessionContextValue;
}

const ChatSessionProviderContent: FC<ChatSessionProviderContentProps> = ({
  children,
  error,
  loading,
  missingSessionProps,
  value,
}) => {
  if (error) {
    return (
      <div className="px-4 py-5">
        <div className="text-destructive">{error}</div>
      </div>
    );
  }

  if (loading && !value.chatSession) {
    return (
      <div className="px-4 py-5">
        <Spinner />
      </div>
    );
  }

  if (!value.chatSession) {
    return <MissingChatSession {...missingSessionProps} />;
  }

  return <ChatSessionContext.Provider value={value}>{children}</ChatSessionContext.Provider>;
};

const ExactChatSessionProvider: FC<PropsWithChildren<Omit<ChatSessionProviderProps, "chatSessionsFilter">>> = ({
  chatSessionId,
  children,
  onClose,
  supportsMultipleSessions = false,
}) => {
  const {
    data,
    loading,
    refetch: refetchChatSession,
  } = useChatSessionQuery({
    variables: { id: chatSessionId! },
    // `network-only` for the FIRST fetch on each mount so the
    // initial render uses fresh server data — not a cached entity
    // that could be stale (the busy gate keys off `chatSession.status`,
    // and stale-idle cached data on remount would unlock the input
    // long enough for the user to type and hit a 409 before the
    // network refresh resolves).
    //
    // `nextFetchPolicy: "cache-and-network"` flips the policy for
    // subsequent reads (poll-driven and refetch-driven) so the
    // server-pushed status changes mirrored into the cache by
    // `useChatSessionStatusLiveCache` (mounted inside `ChatProvider`)
    // are observed without forcing a network round-trip per cache
    // broadcast.
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-and-network",
    // Keep the 3s poll. The subscription is the primary refresh
    // signal, but two transitions don't fire it:
    //   - `tryClaim`'s `READY → ENQUEUED` status write (uses
    //     `updateMany`, doesn't emit `chatSessionStatusChanged`)
    //   - any missed delivery on a transient WebSocket reconnect
    // For a trigger-dispatched turn, the busy-input gate has to lock
    // within a few seconds — a long safety-net poll opens a window
    // where the user can type and hit a 409. Publishing on the
    // claim path properly closes this; tracked as a follow-up.
    pollInterval: 3000,
  });

  const selectedChatSession = useMemo(() => data?.chatSession ?? null, [data?.chatSession]);
  const chatSessions = useMemo(() => (selectedChatSession ? [selectedChatSession] : []), [selectedChatSession]);
  const setChatSession = useCallback<ChatSessionContextValue["setChatSession"]>(() => {}, []);
  const missingSessionProps = useMemo<MissingChatSessionProps>(
    () => ({
      title: "Chat session not found",
      description: "This chat session could not be found.",
    }),
    []
  );

  const value = useMemo<ChatSessionContextValue>(
    () => ({
      canCreateChatSession: false,
      chatSession: selectedChatSession,
      chatSessions,
      chatSessionsLoading: loading,
      createChatSessionLoading: false,
      handleCreateChatSession: async () => {},
      onClose,
      refetchChatSessions: () => {
        void refetchChatSession();
      },
      setChatSession,
      supportsMultipleSessions,
    }),
    [chatSessions, loading, onClose, refetchChatSession, selectedChatSession, setChatSession, supportsMultipleSessions]
  );

  return (
    <ChatSessionProviderContent loading={loading} missingSessionProps={missingSessionProps} value={value}>
      {children}
    </ChatSessionProviderContent>
  );
};

const FilteredChatSessionProvider: FC<PropsWithChildren<Omit<ChatSessionProviderProps, "chatSessionId">>> = ({
  allowCreateChatSession = true,
  chatSessionsFilter,
  children,
  missingSessionProps,
  onClose,
  onSelectSession,
  selectedSessionId,
  supportsMultipleSessions = false,
}) => {
  // Controlled vs uncontrolled selection. Controlled when the consumer
  // wires `onSelectSession` (it owns the selection — e.g. the inbox
  // BookChatPanel keeps it in the URL). Uncontrolled otherwise: the
  // provider holds the selected id in local state. Exactly one store
  // holds the selection in either mode — no snapshot, no cross-store
  // sync effect, so the deep-link / reorder / scope-reset races that
  // plagued the previous design can't form.
  const isControlled = onSelectSession !== undefined;
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const selectedId = isControlled ? (selectedSessionId ?? null) : internalSelectedId;

  const { toast } = useToast();
  const { user: currentUser } = useAccount();

  const chatSessionsFilterKey = useMemo(() => JSON.stringify(chatSessionsFilter ?? {}), [chatSessionsFilter]);
  const stableChatSessionsFilter = useMemo(
    () => JSON.parse(chatSessionsFilterKey) as Omit<ChatSessionFilterInput & CreateChatSessionInput, "userId">,
    [chatSessionsFilterKey]
  );

  // Failure latch, keyed by filter. Once a `create` for the current filter
  // fails, we stop auto-resolving to `create` for it — otherwise the failed
  // create leaves every `resolveChatSelection` input unchanged and the effect
  // below re-fires the mutation on the next render, storming a single
  // rejection into an all-day loop (B-1674). Keying by filter means a
  // different conversation still gets one clean attempt: `createChatSessionFailed`
  // falls back to false as soon as `chatSessionsFilterKey` changes.
  const [failedCreateFilterKey, setFailedCreateFilterKey] = useState<string | null>(null);
  const createChatSessionFailed = failedCreateFilterKey === chatSessionsFilterKey;

  const [createChatSession, { loading: createChatSessionLoading, error: createChatSessionError }] =
    useCreateChatSessionMutation();

  const {
    data: { chatSessions } = { chatSessions: [] },
    loading: chatSessionsLoading,
    refetch: refetchQueriedChatSessions,
  } = useChatSessionsQuery({
    variables: { input: stableChatSessionsFilter },
    // `network-only` first fetch (avoids stale-on-mount race against
    // the busy gate); subsequent reads use `cache-and-network` so
    // server-pushed status changes mirrored into the cache by
    // `useChatSessionStatusLiveCache` (mounted inside `ChatProvider`)
    // are observed. The 3s poll covers list-shape changes (sessions
    // entering/leaving filtered status sets), which cache field updates
    // can't express.
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-and-network",
    pollInterval: 3000,
  });

  const refetchChatSessions = useCallback(() => {
    void refetchQueriedChatSessions();
  }, [refetchQueriedChatSessions]);

  // The one place selection is written. Routes to the consumer in
  // controlled mode, or to local state in uncontrolled mode. Used by
  // `ChatSessionSelector` (manual pick), the adopt-pin effect, and the
  // create completion below.
  const setChatSession = useCallback(
    (next: ChatSession | null | undefined) => {
      if (isControlled) {
        onSelectSession?.(next ?? null);
      } else {
        setInternalSelectedId(next?.id ?? null);
      }
    },
    [isControlled, onSelectSession]
  );

  // Single source of truth for "what should be active", computed purely
  // from the current inputs (see `resolveChatSelection`). The session is
  // rendered immediately for `ready`/`adopt`, so deep-links and defaults
  // show with no flash; the lone effect below applies the side-effecting
  // resolutions.
  const resolution = useMemo(
    () =>
      resolveChatSelection({
        chatSessions,
        selectedId,
        currentUserId: currentUser.id,
        allowCreateChatSession,
        loading: chatSessionsLoading,
        createChatSessionFailed,
      }),
    [chatSessions, selectedId, currentUser.id, allowCreateChatSession, chatSessionsLoading, createChatSessionFailed]
  );

  const chatSession = resolution.kind === "ready" || resolution.kind === "adopt" ? resolution.session : null;

  const handleCreateChatSession = useCallback(async () => {
    if (!allowCreateChatSession) {
      return;
    }

    await createChatSession({
      variables: { input: stableChatSessionsFilter },
      refetchQueries: ["ChatSessions"],
      onCompleted: async (data) => {
        setFailedCreateFilterKey(null);
        setChatSession(data?.createChatSession);
        await refetchQueriedChatSessions();
      },
      onError: (error) => {
        // biome-ignore lint/suspicious/noConsole: intentional
        console.error("Failed to create new chat session:", error);
        // Latch the failure for this filter so the resolution stops auto-firing
        // create (B-1674). One toast, then a stable missing/error panel.
        setFailedCreateFilterKey(chatSessionsFilterKey);
        toast({
          title: "Failed to create new chat session",
          description: "Please try again later",
          variant: "destructive",
        });
      },
    });
  }, [
    allowCreateChatSession,
    chatSessionsFilterKey,
    createChatSession,
    refetchQueriedChatSessions,
    setChatSession,
    stableChatSessionsFilter,
    toast,
  ]);

  // The single side-effecting step, driven entirely by the pure
  // resolution:
  //   - `adopt`  → pin the default into the selection store so a
  //                background list reorder can't swap the visible session
  //                (the session is already rendered via `chatSession`).
  //   - `create` → mint a session for the current user.
  // `ready` / `pending` / `missing` need no effect. Loop-safe: `adopt`
  // writes `selectedId`, the next render resolves `ready`; `create` →
  // refetch yields the user's session, which then resolves `adopt`→`ready`.
  // `createChatSessionLoading` guards against double-firing the mutation.
  useEffect(() => {
    if (resolution.kind === "adopt") {
      setChatSession(resolution.session);
    } else if (resolution.kind === "create" && !createChatSessionLoading) {
      void handleCreateChatSession();
    }
  }, [resolution, createChatSessionLoading, setChatSession, handleCreateChatSession]);

  const value = useMemo<ChatSessionContextValue>(
    () => ({
      canCreateChatSession: allowCreateChatSession,
      chatSession,
      chatSessions,
      chatSessionsLoading,
      createChatSessionLoading,
      handleCreateChatSession,
      onClose,
      refetchChatSessions,
      setChatSession,
      supportsMultipleSessions,
    }),
    [
      allowCreateChatSession,
      chatSession,
      chatSessions,
      chatSessionsLoading,
      createChatSessionLoading,
      handleCreateChatSession,
      onClose,
      refetchChatSessions,
      setChatSession,
      supportsMultipleSessions,
    ]
  );

  return (
    <ChatSessionProviderContent
      // Only surface the create error while there is no session to show.
      // The failure latch stops the retry storm but keeps polling; if a
      // later refetch yields a usable session (resolution → `adopt`), let
      // it render instead of masking the recovered chat with a stale error
      // panel that only clears on remount (B-1674 recovery path).
      error={createChatSessionError && !chatSession ? "Error creating chat session." : undefined}
      // `pending` (list loading) and `create` (about to mint) both render
      // the spinner — no session yet. `missing` falls through to
      // `MissingChatSession`. `ready`/`adopt` render children.
      loading={resolution.kind === "pending" || resolution.kind === "create" || createChatSessionLoading}
      missingSessionProps={!allowCreateChatSession ? missingSessionProps : undefined}
      value={value}
    >
      {children}
    </ChatSessionProviderContent>
  );
};

export const ChatSessionProvider: FC<PropsWithChildren<ChatSessionProviderProps>> = memo(
  ({
    allowCreateChatSession = true,
    chatSessionId,
    chatSessionsFilter,
    children,
    missingSessionProps,
    onClose,
    onSelectSession,
    selectedSessionId,
    supportsMultipleSessions,
  }) => {
    if (chatSessionId) {
      return (
        <ExactChatSessionProvider
          chatSessionId={chatSessionId}
          onClose={onClose}
          supportsMultipleSessions={supportsMultipleSessions}
        >
          {children}
        </ExactChatSessionProvider>
      );
    }

    return (
      <FilteredChatSessionProvider
        allowCreateChatSession={allowCreateChatSession}
        chatSessionsFilter={chatSessionsFilter}
        missingSessionProps={missingSessionProps}
        onClose={onClose}
        onSelectSession={onSelectSession}
        selectedSessionId={selectedSessionId}
        supportsMultipleSessions={supportsMultipleSessions}
      >
        {children}
      </FilteredChatSessionProvider>
    );
  }
);
ChatSessionProvider.displayName = "ChatSessionProvider";

export const useChatSession = () => {
  const context = useContext(ChatSessionContext);

  if (!context) {
    throw new Error("useChatSession must be used within a ChatSessionProvider");
  }

  if (!context.chatSession) {
    throw new Error("Chat session not found");
  }

  return context;
};
